import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Crown, Mail, CheckCircle, XCircle, CreditCard } from 'lucide-react';
import { motion } from 'motion/react';

interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  trialExpired?: boolean;
  featureName?: string;
}

declare global {
  interface Window {
    ezsite: any;
    paypal?: any;
  }
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  isOpen,
  onClose,
  trialExpired = false,
  featureName = 'premium feature'
}) => {
  const [activeTab, setActiveTab] = useState('paypal');

  const handleContactAdmin = async () => {
    try {
      const { error } = await window.ezsite.apis.sendEmail({
        from: 'System <noreply@eduai.com>',
        to: ['admin@eduai.com'],
        subject: 'Upgrade Request - Premium Access',
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #333;">Upgrade Request</h2>
            <p>A user has requested to upgrade to premium access:</p>
            <div style="background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0;">
              <strong>Feature Requested:</strong> ${featureName}<br>
              <strong>Reason:</strong> ${trialExpired ? 'Trial period expired' : 'Premium feature access'}
            </div>
            <p>Please contact the user to process their upgrade request.</p>
          </div>
        `
      });

      if (error) throw error;

      alert('Upgrade request sent! An admin will contact you soon.');
      onClose();
    } catch (error) {
      console.error('Error sending upgrade request:', error);
      alert('Failed to send upgrade request. Please try again.');
    }
  };

  useEffect(() => {
    const loadPayPalScriptAndRenderButton = () => {
      const scriptId = 'paypal-sdk-script';

      const renderButton = () => {
        const container = document.getElementById('paypal-button-container');
        if (window.paypal && container) {
          container.innerHTML = ''; // Clear any previously rendered buttons
          window.paypal.Buttons({
            style: {
              shape: 'rect',
              color: 'gold',
              layout: 'vertical',
              label: 'subscribe'
            },
            createSubscription: function (data: any, actions: any) {
              return actions.subscription.create({
                plan_id: 'P-5NU26225C1187290DNB62IEA'
              });
            },
            onApprove: function (data: any, actions: any) {
              alert(data.subscriptionID);
              onClose();
            }
          }).render('#paypal-button-container');
        } else {
          setTimeout(renderButton, 100);
        }
      };

      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://www.paypal.com/sdk/js?client-id=Ab3iofQucOsvAl7Wo-1UZjPstqls7jdt58YgOhdcPQ-jVk0oGaA-yGstQ7NX4DJEfzKGd4eULnTOsaYj&vault=true&intent=subscription';
        script.setAttribute('data-sdk-integration-source', 'button-factory');
        script.onload = renderButton;
        document.body.appendChild(script);
      } else {
        renderButton();
      }
    };

    if (activeTab === 'paypal') {
      loadPayPalScriptAndRenderButton();
    }
  }, [activeTab, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose} data-id="8n2j3q20u" data-path="src/components/trial/UpgradePrompt.tsx">
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-id="jcbscacxh" data-path="src/components/trial/UpgradePrompt.tsx">
        <DialogHeader data-id="o9625jpc4" data-path="src/components/trial/UpgradePrompt.tsx">
          <DialogTitle className="flex items-center gap-2" data-id="5vyq8snjd" data-path="src/components/trial/UpgradePrompt.tsx">
            <Crown className="h-5 w-5 text-purple-600" data-id="daijtwge3" data-path="src/components/trial/UpgradePrompt.tsx" />
            {trialExpired ? 'Trial Period Expired' : 'Premium Feature Required'}
          </DialogTitle>
          <DialogDescription data-id="dvfm94x8n" data-path="src/components/trial/UpgradePrompt.tsx">
            {trialExpired ?
            'Your 7-day trial has ended. Upgrade to continue using all features.' :
            `Access to "${featureName}" requires a premium subscription.`}
          </DialogDescription>
        </DialogHeader>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4" data-id="wdq6jpa1w" data-path="src/components/trial/UpgradePrompt.tsx">

          <Card className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200" data-id="le3xpohzf" data-path="src/components/trial/UpgradePrompt.tsx">
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2" data-id="38ogfota8" data-path="src/components/trial/UpgradePrompt.tsx">
              <Crown className="h-4 w-4 text-purple-600" data-id="petyol53m" data-path="src/components/trial/UpgradePrompt.tsx" />
              Premium Benefits
            </h3>
            <div className="space-y-2" data-id="99imadr3o" data-path="src/components/trial/UpgradePrompt.tsx">
              <div className="flex items-center gap-2 text-sm" data-id="qkdq0z74w" data-path="src/components/trial/UpgradePrompt.tsx">
                <CheckCircle className="h-4 w-4 text-green-600" data-id="4pzunrkoz" data-path="src/components/trial/UpgradePrompt.tsx" />
                <span data-id="979l6ylig" data-path="src/components/trial/UpgradePrompt.tsx">Unlimited AI-powered tools</span>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="w2icolyp3" data-path="src/components/trial/UpgradePrompt.tsx">
                <CheckCircle className="h-4 w-4 text-green-600" data-id="2cjmq2q93" data-path="src/components/trial/UpgradePrompt.tsx" />
                <span data-id="npzhyu1qk" data-path="src/components/trial/UpgradePrompt.tsx">Advanced lesson plan generation</span>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="4p5i6bewk" data-path="src/components/trial/UpgradePrompt.tsx">
                <CheckCircle className="h-4 w-4 text-green-600" data-id="i9q07ue79" data-path="src/components/trial/UpgradePrompt.tsx" />
                <span data-id="wjfk66th8" data-path="src/components/trial/UpgradePrompt.tsx">Premium slide templates</span>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="42yci4ibu" data-path="src/components/trial/UpgradePrompt.tsx">
                <CheckCircle className="h-4 w-4 text-green-600" data-id="gdpzxad2z" data-path="src/components/trial/UpgradePrompt.tsx" />
                <span data-id="7znc4l4ov" data-path="src/components/trial/UpgradePrompt.tsx">Priority support</span>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="sxvc169ym" data-path="src/components/trial/UpgradePrompt.tsx">
                <CheckCircle className="h-4 w-4 text-green-600" data-id="cllt6lloq" data-path="src/components/trial/UpgradePrompt.tsx" />
                <span data-id="d8tojnnak" data-path="src/components/trial/UpgradePrompt.tsx">Export capabilities</span>
              </div>
            </div>
          </Card>

          {trialExpired &&
          <Card className="p-3 border-l-4 border-l-orange-500 bg-orange-50" data-id="pi1ev3kxo" data-path="src/components/trial/UpgradePrompt.tsx">
              <div className="flex items-center gap-2 text-orange-800" data-id="gz1s3qkve" data-path="src/components/trial/UpgradePrompt.tsx">
                <XCircle className="h-4 w-4" data-id="i7jzml6hv" data-path="src/components/trial/UpgradePrompt.tsx" />
                <span className="text-sm font-medium" data-id="3qvnxp2gu" data-path="src/components/trial/UpgradePrompt.tsx">
                  Your trial ended. Continue with premium access!
                </span>
              </div>
            </Card>
          }

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-id="m4aj62hi4" data-path="src/components/trial/UpgradePrompt.tsx">
            <TabsList className="grid w-full grid-cols-2" data-id="ik551o7w2" data-path="src/components/trial/UpgradePrompt.tsx">
              <TabsTrigger value="paypal" className="flex items-center gap-2" data-id="nr0x5o9z1" data-path="src/components/trial/UpgradePrompt.tsx">
                <CreditCard className="h-4 w-4" data-id="ws6ag2mpo" data-path="src/components/trial/UpgradePrompt.tsx" />
                PayPal Payment
              </TabsTrigger>
              <TabsTrigger value="admin" className="flex items-center gap-2" data-id="cin1qe4s2" data-path="src/components/trial/UpgradePrompt.tsx">
                <Mail className="h-4 w-4" data-id="jfsech9w3" data-path="src/components/trial/UpgradePrompt.tsx" />
                Contact Admin
              </TabsTrigger>
            </TabsList>

            <TabsContent value="paypal" className="mt-4" data-id="3l8d9efu3" data-path="src/components/trial/UpgradePrompt.tsx">
              <div id="paypal-button-container" data-id="dbf9qxlqq" data-path="src/components/trial/UpgradePrompt.tsx" />
            </TabsContent>

            <TabsContent value="admin" className="mt-4" data-id="n9pb2k2em" data-path="src/components/trial/UpgradePrompt.tsx">
              <Card className="p-4 bg-gray-50" data-id="lkjarffyj" data-path="src/components/trial/UpgradePrompt.tsx">
                <p className="text-sm text-gray-700 mb-4" data-id="oalijcf9i" data-path="src/components/trial/UpgradePrompt.tsx">
                  Prefer to upgrade through an administrator? Send a request and we'll get back to you soon.
                </p>
                <div className="flex gap-3" data-id="wohfvgq1i" data-path="src/components/trial/UpgradePrompt.tsx">
                  <Button
                    onClick={handleContactAdmin}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700" data-id="lwarniw99" data-path="src/components/trial/UpgradePrompt.tsx">

                    <Mail className="h-4 w-4 mr-2" data-id="8lah5sonb" data-path="src/components/trial/UpgradePrompt.tsx" />
                    Contact Admin to Upgrade
                  </Button>
                </div>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end mt-4" data-id="sxeqca250" data-path="src/components/trial/UpgradePrompt.tsx">
            <Button variant="outline" onClick={onClose} data-id="twrek6n80" data-path="src/components/trial/UpgradePrompt.tsx">
              Later
            </Button>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>);

};

export default UpgradePrompt;